import { useEffect, useMemo, useState } from "react";
import { Backdrop, Button, CircularProgress, TextField, Typography } from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";

import MultiSelectCheck from "components/atoms/MultiSelectField/MultSelectCheck";
import { MultiSelectOption } from "components/atoms/MultiSelectField/MultiSelectOptions";
import {
  findAcoesRelatorioGeral,
  findRelatorioInscritosPorMunicipio,
  findRelatorioInscritosPorMunicipioCount,
  findTurmasRelatorioGeral,
  RelatorioInscritosPorMunicipioParams,
} from "API/apiRelatoriosTurma";
import { useAppFeedback } from "hooks/useAppFeedback";
import stylesButton from "styles/Button.module.css";
import stylesInput from "styles/Input.module.css";
import { getReportPeriodError } from "utils/reportPeriod";
import { addGovernmentReportLogo, prependGovernmentReportHeader } from "utils/reportExportHeader";

import styles from "../Relatorios.module.css";

type RelatorioInscritosPorMunicipioProps = {
  selectedReportLabel: string;
};

type MunicipioItem = {
  id?: number | null;
  nome?: string | null;
  limite_estadual?: number | null;
  limite_municipal?: number | null;
  vagas_disponibilizadas?: number | null;
  vagas_ocupadas?: number | null;
  vagas_ocupadas_estadual?: number | null;
  vagas_ocupadas_municipal?: number | null;
};

type RelatorioInscritosTurmaItem = {
  acao_id?: number | null;
  acao_nome?: string | null;
  turma_id?: number | null;
  turma_nome?: string | null;
  quantidade_total_vagas?: number | null;
  criterio_ocupacao?: string | null;
  limite_ambito_estadual?: number | null;
  limite_ambito_municipal?: number | null;
  limites?: {
    modo?: string | null;
    sem_limite?: boolean | null;
    mesmo_limite_para_todos?: boolean | null;
  };
  municipios?: MunicipioItem[];
};

type ExportRow = {
  acao_nome: string;
  turma_nome: string;
  municipio_nome: string;
  quantidade_total_vagas: number | string;
  criterio_ocupacao: string;
  vagas_ambito_municipal: number | string;
  vagas_ambito_estadual: number | string;
  distribuicao_vagas: string;
  qtd_vagas_municipais: number | string;
  vagas_municipais_ocupadas: number | string;
  qtd_vagas_estaduais: number | string;
  vagas_estaduais_ocupadas: number | string;
};

const exportColumns: Array<{ header: string; key: keyof ExportRow; width: number }> = [
  { header: "NOME DA AÇÃO", key: "acao_nome", width: 34 },
  { header: "TURMA", key: "turma_nome", width: 32 },
  { header: "MUNICÍPIO", key: "municipio_nome", width: 28 },
  { header: "QUANTIDADE TOTAL DE VAGAS", key: "quantidade_total_vagas", width: 20 },
  { header: "CRITÉRIO DE OCUPAÇÃO", key: "criterio_ocupacao", width: 22 },
  { header: "VAGAS ÂMBITO MUNICIPAL", key: "vagas_ambito_municipal", width: 20 },
  { header: "VAGAS ÂMBITO ESTADUAL", key: "vagas_ambito_estadual", width: 20 },
  { header: "DISTRIBUIÇÃO VAGAS", key: "distribuicao_vagas", width: 18 },
  { header: "QTD VAGAS MUNICIPAIS", key: "qtd_vagas_municipais", width: 18 },
  { header: "VAGAS MUNICIPAIS OCUPADAS", key: "vagas_municipais_ocupadas", width: 20 },
  { header: "QTD VAGAS ESTADUAIS", key: "qtd_vagas_estaduais", width: 18 },
  { header: "VAGAS ESTADUAIS OCUPADAS", key: "vagas_estaduais_ocupadas", width: 20 },
];

const formatDate = (value?: string | null) => {
  if (!value) return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "";
};

const formatNumberOrDash = (value?: number | null) => (value === null || value === undefined ? "-" : value);

const normalizeDistribuicaoModo = (value?: string | null) => {
  if (!value) return "-";
  if (value === "SEM_LIMITE") return "SEM_LIMITE";
  if (value === "IGUAL") return "IGUAL";
  if (value === "DIFERENTE") return "DIFERENTE";
  return value;
};

const buildHeader = (worksheet: ExcelJS.Worksheet, columnsLength: number, headerRowNumber = 1) => {
  const headerRow = worksheet.getRow(headerRowNumber);
  headerRow.height = 22;
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF004D40" },
    };
    cell.font = {
      bold: true,
      color: { argb: "FFFFFFFF" },
    };
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };
  });

  worksheet.autoFilter = {
    from: { row: headerRowNumber, column: 1 },
    to: { row: headerRowNumber, column: columnsLength },
  };
};

const RelatorioInscritosPorMunicipio = ({
  selectedReportLabel,
}: RelatorioInscritosPorMunicipioProps) => {
  const { showError, showSuccess } = useAppFeedback();
  const [dataInicial, setDataInicial] = useState("");
  const [dataFinal, setDataFinal] = useState("");
  const [acoesSelecionadas, setAcoesSelecionadas] = useState<Array<number | string>>([]);
  const [turmasSelecionadas, setTurmasSelecionadas] = useState<Array<number | string>>([]);
  const [acoesOptions, setAcoesOptions] = useState<MultiSelectOption[]>([]);
  const [turmasOptions, setTurmasOptions] = useState<MultiSelectOption[]>([]);
  const [loadingAcoes, setLoadingAcoes] = useState(false);
  const [loadingTurmas, setLoadingTurmas] = useState(false);
  const [loadingCount, setLoadingCount] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [estimatedCount, setEstimatedCount] = useState<number | null>(null);

  const periodError = getReportPeriodError(dataInicial, dataFinal);
  const periodValidationError = dataInicial && dataFinal ? periodError : null;
  const hasValidPeriod = !periodError;
  const canSelectAcoes = hasValidPeriod && !loadingAcoes;
  const canSelectTurmas = acoesSelecionadas.length > 0 && !loadingTurmas;

  const selectedActionIds = useMemo(
    () => acoesSelecionadas.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [acoesSelecionadas],
  );
  const selectedTurmaIds = useMemo(
    () => turmasSelecionadas.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [turmasSelecionadas],
  );

  useEffect(() => {
    if (!hasValidPeriod) {
      setAcoesOptions([]);
      setTurmasOptions([]);
      setAcoesSelecionadas([]);
      setTurmasSelecionadas([]);
      setEstimatedCount(null);
      return;
    }

    const loadAcoes = async () => {
      try {
        setLoadingAcoes(true);
        const response = await findAcoesRelatorioGeral(dataInicial, dataFinal);
        setAcoesOptions((response.acoes || []).map((item) => ({ id: item.id, nome: item.nome })));
        setAcoesSelecionadas([]);
        setTurmasSelecionadas([]);
        setTurmasOptions([]);
        setEstimatedCount(null);
      } catch (error) {
        setAcoesOptions([]);
        setTurmasOptions([]);
        setAcoesSelecionadas([]);
        setTurmasSelecionadas([]);
        setEstimatedCount(null);
        showError("Não foi possível carregar as ações do relatório.", error);
      } finally {
        setLoadingAcoes(false);
      }
    };

    void loadAcoes();
  }, [dataFinal, dataInicial, hasValidPeriod, showError]);

  useEffect(() => {
    if (!hasValidPeriod || selectedActionIds.length === 0) {
      setTurmasOptions([]);
      setTurmasSelecionadas([]);
      return;
    }

    const loadTurmas = async () => {
      try {
        setLoadingTurmas(true);
        const response = await findTurmasRelatorioGeral(selectedActionIds, dataInicial, dataFinal);
        const turmas = response.turmas || [];
        setTurmasOptions(turmas.map((item) => ({ id: item.id, nome: item.nome })));
        setTurmasSelecionadas((current) =>
          current.filter((id) => turmas.some((turma) => turma.id === Number(id))),
        );
      } catch (error) {
        setTurmasOptions([]);
        setTurmasSelecionadas([]);
        showError("Não foi possível carregar as turmas do relatório.", error);
      } finally {
        setLoadingTurmas(false);
      }
    };

    void loadTurmas();
  }, [dataFinal, dataInicial, hasValidPeriod, selectedActionIds, showError]);

  const requestParams = useMemo<RelatorioInscritosPorMunicipioParams | null>(() => {
    if (periodError || selectedActionIds.length === 0) {
      return null;
    }

    return {
      data_inicio: dataInicial,
      data_fim: dataFinal,
      acoes_ids: selectedActionIds,
      turmas_ids: selectedTurmaIds.length > 0 ? selectedTurmaIds : undefined,
    };
  }, [dataFinal, dataInicial, periodError, selectedActionIds, selectedTurmaIds]);

  useEffect(() => {
    if (!requestParams) {
      setEstimatedCount(null);
      return;
    }

    const loadCount = async () => {
      try {
        setLoadingCount(true);
        const response = await findRelatorioInscritosPorMunicipioCount(requestParams);
        setEstimatedCount(response.count ?? 0);
      } catch (error) {
        setEstimatedCount(null);
        showError("Não foi possível calcular o resultado estimado do relatório.", error);
      } finally {
        setLoadingCount(false);
      }
    };

    void loadCount();
  }, [requestParams, showError]);

  const handleClear = () => {
    setDataInicial("");
    setDataFinal("");
    setAcoesSelecionadas([]);
    setTurmasSelecionadas([]);
    setAcoesOptions([]);
    setTurmasOptions([]);
    setEstimatedCount(null);
  };

  const buildExportRows = (items: RelatorioInscritosTurmaItem[]): ExportRow[] =>
    items.flatMap((item) =>
      (item.municipios || []).map((municipio) => ({
        acao_nome: item.acao_nome || "",
        turma_nome: item.turma_nome || "",
        municipio_nome: municipio.nome || "",
        quantidade_total_vagas: item.quantidade_total_vagas ?? 0,
        criterio_ocupacao: item.criterio_ocupacao || "",
        vagas_ambito_municipal: formatNumberOrDash(item.limite_ambito_municipal),
        vagas_ambito_estadual: formatNumberOrDash(item.limite_ambito_estadual),
        distribuicao_vagas: normalizeDistribuicaoModo(item.limites?.modo),
        qtd_vagas_municipais: item.limites?.sem_limite ? "-" : formatNumberOrDash(municipio.limite_municipal),
        vagas_municipais_ocupadas: municipio.vagas_ocupadas_municipal ?? 0,
        qtd_vagas_estaduais: item.limites?.sem_limite ? "-" : formatNumberOrDash(municipio.limite_estadual),
        vagas_estaduais_ocupadas: municipio.vagas_ocupadas_estadual ?? 0,
      })),
    );

  const buildWorkbook = (rows: ExportRow[]) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Inscritos por Municipio");

    worksheet.columns = exportColumns.map((column) => ({
      header: column.header,
      key: column.key,
      width: column.width,
    }));

    const headerRowNumber = prependGovernmentReportHeader(worksheet, exportColumns.length) + 1;
    buildHeader(worksheet, exportColumns.length, headerRowNumber);

    rows.forEach((row) => {
      worksheet.addRow(row);
    });

    return workbook;
  };

  const handleGenerateReport = async () => {
    if (periodError) {
      showError(periodError);
      return;
    }

    if (!requestParams) {
      showError("Informe o período e selecione ao menos uma ação para gerar o relatório.");
      return;
    }

    try {
      setGeneratingReport(true);
      const response = await findRelatorioInscritosPorMunicipio(requestParams);
      const exportRows = buildExportRows((response.data || []) as RelatorioInscritosTurmaItem[]);
      const workbook = buildWorkbook(exportRows);
      await Promise.all(workbook.worksheets.map((worksheet) => addGovernmentReportLogo(workbook, worksheet)));
      const excelBuffer = await workbook.xlsx.writeBuffer();

      saveAs(
        new Blob([excelBuffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        "relatorio_inscritos_por_municipio.xlsx",
      );

      showSuccess("Relatório gerado com sucesso.");
    } catch (error) {
      showError("Não foi possível gerar o relatório.", error);
    } finally {
      setGeneratingReport(false);
    }
  };

  return (
    <>
      <div className={styles.reportFilterBody}>
        <div className={styles.reportHeaderRow}>
          <div className={styles.reportSelectedWrap}>
            <span className={styles.reportSelectedLabel}>Relatório selecionado:</span>
            <span className={styles.reportSelectedChip}>{selectedReportLabel}</span>
          </div>

          <div className={styles.exportFormatCard}>
            <div className={styles.exportFormatLabel}>Formato de exportação</div>
            <div className={styles.exportFormatValue}>
              <TableChartOutlinedIcon className={styles.exportFormatIcon} />
              XLSX
            </div>
          </div>
        </div>

        <div className={styles.reportDivider}></div>

        <div className={styles.filterSection}>
          <div className={styles.filterBlockTitle}>
            Período <span className={styles.optionalText}>(obrigatório)</span>
          </div>
          <div className={styles.filterGridTwo}>
            <div>
              <label className={stylesInput.titleInputSoft}>Data inicial</label>
              <TextField
                type="date"
                fullWidth
                size="small"
                variant="standard"
                value={dataInicial}
                error={Boolean(periodValidationError)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDataInicial(event.target.value)}
                InputProps={{ className: stylesInput.inputSoft, disableUnderline: true }}
              />
            </div>

            <div>
              <label className={stylesInput.titleInputSoft}>Data final</label>
              <TextField
                type="date"
                fullWidth
                size="small"
                variant="standard"
                value={dataFinal}
                error={Boolean(periodValidationError)}
                helperText={periodValidationError || undefined}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDataFinal(event.target.value)}
                InputProps={{ className: stylesInput.inputSoft, disableUnderline: true }}
              />
            </div>
          </div>
        </div>

        <div className={styles.filterSection}>
          <div className={styles.filterBlockTitle}>
            Selecione a(s) ação(ões) <span className={styles.optionalText}>(obrigatório)</span>
          </div>
          <MultiSelectCheck
            label=""
            placeholder={
              hasValidPeriod ? "Selecione uma ou mais ações" : "Preencha primeiro a data inicial e a data final"
            }
            fullWidth
            value={acoesSelecionadas}
            onChange={setAcoesSelecionadas}
            options={acoesOptions}
            disabled={!canSelectAcoes}
            selectedDisplayMode="count"
            paperClassname={stylesInput.paper}
            popperClassname={stylesInput.popper}
            titleInputClassname={styles.reportHiddenLabel}
            inputSelectClassname={stylesInput.inputSoft}
            labelSelectClassname={stylesInput.labelSelect}
          />
        </div>

        <div className={styles.filterSection}>
          <div className={styles.filterBlockTitle}>
            Selecione a(s) turma(s) <span className={styles.optionalText}>(opcional)</span>
          </div>
          <MultiSelectCheck
            label=""
            placeholder={
              selectedActionIds.length > 0
                ? "Selecione uma ou mais turmas (deixe vazio para todas as turmas das ações selecionadas)"
                : "Selecione primeiro uma ou mais ações"
            }
            fullWidth
            value={turmasSelecionadas}
            onChange={setTurmasSelecionadas}
            options={turmasOptions}
            disabled={!canSelectTurmas}
            selectedDisplayMode="count"
            paperClassname={stylesInput.paper}
            popperClassname={stylesInput.popper}
            titleInputClassname={styles.reportHiddenLabel}
            inputSelectClassname={stylesInput.inputSoft}
            labelSelectClassname={stylesInput.labelSelect}
          />
        </div>

        <div className={styles.reportInfoBanner}>
          <InfoOutlinedIcon className={styles.reportInfoBannerIcon} />
          <span>
            Cada linha do XLSX representará um município da turma. Ao deixar turmas vazio, o sistema considera todas as
            turmas das ações selecionadas.
          </span>
        </div>

        <div className={styles.selectionSummaryCard}>
          <div className={styles.selectionSummaryTitle}>Resumo da seleção</div>

          <div className={styles.selectionSummaryGrid}>
            <div className={styles.selectionSummaryItem}>
              <CalendarMonthOutlinedIcon className={styles.selectionSummaryIcon} />
              <div>
                <div className={styles.selectionSummaryLabel}>Período</div>
                <div className={styles.selectionSummaryValue}>
                  {formatDate(dataInicial)} a {formatDate(dataFinal)}
                </div>
              </div>
            </div>

            <div className={styles.selectionSummaryItem}>
              <AppsOutlinedIcon className={styles.selectionSummaryIcon} />
              <div>
                <div className={styles.selectionSummaryLabel}>Ações selecionadas</div>
                <div className={styles.selectionSummaryValue}>{selectedActionIds.length} ação(ões)</div>
              </div>
            </div>

            <div className={styles.selectionSummaryItem}>
              <GroupsOutlinedIcon className={styles.selectionSummaryIcon} />
              <div>
                <div className={styles.selectionSummaryLabel}>Turmas selecionadas</div>
                <div className={styles.selectionSummaryValue}>
                  {selectedTurmaIds.length > 0 ? `${selectedTurmaIds.length} turma(s)` : "Todas as turmas"}
                </div>
              </div>
            </div>

            <div className={styles.selectionSummaryItem}>
              <PlaceOutlinedIcon className={styles.selectionSummaryIcon} />
              <div>
                <div className={styles.selectionSummaryLabel}>Saída</div>
                <div className={styles.selectionSummaryValue}>Uma linha por município</div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.reportActionsRow}>
          <Button
            variant="outlined"
            className={stylesButton.buttonSoftMuted}
            startIcon={<DeleteOutlineOutlinedIcon />}
            onClick={handleClear}
          >
            Limpar filtros
          </Button>

          <div className={styles.reportActionsRight}>
            <div className={styles.estimatedResultCard}>
              <div className={styles.estimatedResultLabel}>Resultado estimado</div>
              <div className={styles.estimatedResultValue}>
                {loadingCount ? <CircularProgress size={18} /> : estimatedCount ?? "-"}
              </div>
              <div className={styles.estimatedResultMeta}>registros</div>
            </div>

            <div className={styles.generateButtonGroup}>
              <Button
                variant="contained"
                className={styles.generateButton}
                startIcon={<DownloadOutlinedIcon />}
                onClick={handleGenerateReport}
                disabled={generatingReport}
              >
                Gerar relatório
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Backdrop
        open={generatingReport}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 100,
          flexDirection: "column",
          gap: 2,
          backgroundColor: "rgba(15, 23, 42, 0.45)",
        }}
      >
        <CircularProgress color="inherit" />
        <Typography sx={{ fontWeight: 600 }}>Gerando relatório...</Typography>
      </Backdrop>
    </>
  );
};

export default RelatorioInscritosPorMunicipio;
